/**
 * Seitenlokale Marketing-Tokens der Willkommensseite (nur `/welcome`).
 *
 * Die dunkelblaue/grüne Farbwelt des Entwurfs (`landing-page/`) wird NICHT als
 * zweites globales Theme eingeführt: Das zentrale App-Theme bleibt die einzige
 * globale Quelle; dieses Modul stellt nur Tokens bereit, die ausschließlich von
 * der Willkommensseite verwendet werden.
 */

import { createTheme, type Theme } from "@mui/material/styles";

export const welcomeTokens = {
  primary: "#123e68", // dunkelblau (Entwurf)
  primaryDark: "#0a2c4d",
  accent: "#45a653", // grün (Entwurf)
  accentDark: "#3f9c4c",
} as const;

/** Sektions- und Akzentfarben je Theme-Modus (hell/dunkel). */
export function welcomeColors(mode: "light" | "dark") {
  if (mode === "dark") {
    return {
      pageBackground: "transparent",
      headerBackground: "rgba(15, 22, 34, 0.9)",
      border: "rgba(255, 255, 255, 0.12)",
      surfaceSoft: "rgba(69, 166, 83, 0.12)",
      accentSoft: "rgba(69, 166, 83, 0.18)",
      accentText: "#8fdda0",
      primarySurface: "rgba(18, 62, 104, 0.45)",
      onPrimary: "inherit",
    } as const;
  }
  return {
    pageBackground: "#f7faf8",
    headerBackground: "rgba(247, 250, 248, 0.9)",
    border: "#e5ece7",
    surfaceSoft: "#edf6f0",
    accentSoft: "#e5f4e7",
    accentText: "#267c36",
    primarySurface: "#123e68",
    onPrimary: "#ffffff",
  } as const;
}

/**
 * Baut ein von `outer` (zentrales App-Theme) abgeleitetes, seitenlokales Theme
 * für die Willkommensseite. Erbt Breakpoints, Spacing und Typografie-Basis vom
 * zentralen Theme und überschreibt nur Marketing-relevante Tokens.
 */
export function createWelcomeTheme(outer: Theme): Theme {
  const dark = outer.palette.mode === "dark";
  return createTheme(outer, {
    palette: {
      primary: {
        main: dark ? "#8fc1e8" : welcomeTokens.primary,
        dark: welcomeTokens.primaryDark,
      },
      secondary: {
        main: dark ? "#7ecf8e" : welcomeTokens.accent,
        dark: welcomeTokens.accentDark,
      },
    },
    shape: { borderRadius: 18 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 999 }, // Pill-Buttons
          // Dunkelmodus: Contained-Buttons dezenter — das helle Primärblau
          // sticht auf dunklen Flächen zu stark hervor. Text-/Icon-Farben
          // (primary.main) bleiben unberührt.
          ...(dark && {
            contained: {
              backgroundColor: "#2f6396",
              color: "#ffffff",
              "&:hover": { backgroundColor: "#285783" },
            },
          }),
        },
      },
    },
  });
}
